import type { AxiosRequestConfig } from "axios"
import { z } from "zod"
import * as Sentry from "@sentry/react"
import client from "./client"

const baseApiResponseSchema = z.object({
  data: z.unknown(),
  message: z.string().optional(),
  success: z.boolean().default(true),
})

export async function fetchWithValidation<T>(
  url: string,
  schema: z.ZodSchema<T>,
  options?: AxiosRequestConfig,
): Promise<T> {
  try {
    const response = await client.request({
      url,
      method: options?.method || "get",
      ...options,
    })

    const baseResult = baseApiResponseSchema.safeParse(response.data)
    if (!baseResult.success) {
      console.error("❌ Invalid base API structure", baseResult.error)
      throw new Error("Unexpected API response format")
    }

    const { data, message, success } = baseResult.data

    if (!success) {
      throw new Error(message || "Request failed")
    }


    const result = schema.safeParse(data)
    if (!result.success) {
      console.error("❌ Zod validation failed", result.error)

      Sentry.captureException(result.error, {
        extra: {
          url,
          data,
        },
      })

      return data as T
    }

    return result.data
  } catch (err: any) {
    console.error("❌ API request failed", err)
    throw err instanceof Error ? err : new Error("Unknown API error")
  }
}
